"use client";

import { CheckIcon, TicketsIcon } from "lucide-react";
import type { PackTenderPreview } from "@/actions/packs";
import { formatPrice } from "@/lib/currency";
import { cn } from "@/lib/utils";

/**
 * Choosing to pay a booking with a pack instead of money.
 *
 * Two rules shape this control:
 *
 * 1. **It is a choice, never an automatic substitution.** Somebody holding a
 *    ten-class pack may still want to pay cash for a friend's ticket and keep
 *    their credits for themselves. Spending a member's credits because it
 *    happened to be possible is spending their money without asking.
 * 2. **A pack that cannot pay is shown greyed with its reason, not hidden.**
 *    "Works Mon-Fri before 5pm" is the difference between a member who
 *    understands their pack and a support ticket. Hiding the row just makes the
 *    pack they paid for look like it vanished.
 */
export function PackTenderSelector({
  preview,
  selectedHoldingId,
  onSelect,
  currency,
  payableMinor,
  disabled = false,
}: {
  preview: PackTenderPreview | null;
  selectedHoldingId: number | null;
  onSelect: (holdingId: number | null) => void;
  currency: string;
  /** What this booking costs in money, for the "pay normally" row. */
  payableMinor: number;
  disabled?: boolean;
}) {
  const option = preview?.option ?? null;
  const groupName = `pack-tender-${option?.holdingId ?? "none"}`;
  const unavailable = preview?.unavailable ?? [];

  // Nothing to choose between: no pack can pay and none needs explaining.
  if (!option && unavailable.length === 0) return null;

  // With no usable pack there is nothing to choose, so the heading must not
  // promise a choice — the rows below are an explanation of why a pack the
  // member owns is not on offer, not a payment method they can pick.
  return (
    <section className="space-y-4 pt-6">
      <h2 className="text-[1.4rem] font-semibold text-text-primary sm:text-[1.55rem]">
        {option ? "How you're paying" : "Your packs"}
      </h2>

      <div className="space-y-2">
        {option ? (
          <>
            <TenderRow
              selected={selectedHoldingId === option.holdingId}
              disabled={disabled}
              onSelect={() => onSelect(option.holdingId)}
              name={groupName}
              title={option.packName}
              // Zero credits is an included entitlement, not an arithmetic
              // slip, and "0 credits from your pack" reads as the latter —
              // the number a member is looking for confirmation on is the one
              // that is about to leave their balance, and none is.
              detail={
                option.credits === 0
                  ? "Included with your pass — uses no credits"
                  : `${option.credits} credit${option.credits === 1 ? "" : "s"} from your pack`
              }
              trailing="No payment"
            />
            <TenderRow
              selected={selectedHoldingId === null}
              disabled={disabled}
              onSelect={() => onSelect(null)}
              name={groupName}
              title="Pay now"
              // There are no credits to keep when the cover is free, so the
              // usual reason to pay instead does not apply — offering it as
              // one invites somebody to pay ₹1,200 to save nothing.
              detail={
                option.credits === 0
                  ? "Pay for this separately instead"
                  : "Keep your pack credits for another class"
              }
              trailing={formatPrice(payableMinor, currency)}
            />
          </>
        ) : null}

        {unavailable.map((rejection) => (
          <div
            key={`${rejection.packName}-${rejection.reason}`}
            className="flex items-start gap-3 rounded-[1.4rem] border border-dashed border-border px-3.5 py-3 dark:border-white/[0.09]"
          >
            <TicketsIcon className="mt-0.5 size-4 shrink-0 text-text-tertiary dark:text-white/40" />
            {/* The message already names the pack — the engine composes it as
                "<Pack> works Mon-Fri before 5pm" — so a separate name line
                above it printed the name twice. */}
            <p className="min-w-0 text-sm text-text-secondary dark:text-white/60">
              {rejection.message}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function TenderRow({
  selected,
  disabled,
  onSelect,
  name,
  title,
  detail,
  trailing,
}: {
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  /** Shared across the group, so the two rows are one radio set. */
  name: string;
  title: string;
  detail: string;
  trailing: string;
}) {
  // A real `<input type="radio">` rather than a button with `role="radio"`:
  // it brings arrow-key navigation within the group, the correct screen-reader
  // announcement, and mutual exclusion, none of which a styled button gets for
  // free. The input is visually hidden but never `display: none`, so it stays
  // focusable — `peer-focus-visible` paints the ring on the row instead.
  return (
    <label
      className={cn(
        "flex w-full cursor-pointer items-center gap-3 rounded-[1.4rem] border px-3.5 py-3 text-left transition",
        "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand/45",
        disabled && "cursor-not-allowed opacity-60",
        selected
          ? "border-brand bg-brand/[0.06] dark:border-white/45 dark:bg-white/[0.06]"
          : "border-border bg-surface hover:bg-surface-secondary dark:border-white/[0.09] dark:bg-white/[0.025] dark:hover:bg-white/[0.05]",
      )}
    >
      <input
        type="radio"
        name={name}
        checked={selected}
        disabled={disabled}
        onChange={onSelect}
        className="sr-only"
      />
      <span
        aria-hidden
        className={cn(
          "flex size-5 shrink-0 items-center justify-center rounded-full border transition",
          selected
            ? "border-brand bg-brand text-brand-foreground dark:border-white dark:bg-white dark:text-black"
            : "border-border dark:border-white/25",
        )}
      >
        {selected ? <CheckIcon className="size-3" strokeWidth={3} /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-text-primary dark:text-white">
          {title}
        </span>
        <span className="block text-xs text-text-tertiary dark:text-white/50">
          {detail}
        </span>
      </span>
      <span className="shrink-0 text-sm font-semibold text-text-primary tabular-nums dark:text-white">
        {trailing}
      </span>
    </label>
  );
}
